<template>
  <PopDialog2
    title="방 정보 수정"
    :visible="props.visible"
    :cancelCallback="onCancelClick"
    compact
  >
    <div class="md-layout">
      <div class="md-layout-item md-size-50 mx-auto text-center light-item-modal-content">

        <div class="light-item-modal-top">
          <h2 class="light-item-modal-title">
            {{ props.roomName }} 조명
          </h2>
          <button
            type="button"
            class="light-item-power-btn"
            :class="isLightOn ? 'light-item-power-btn--on' : 'light-item-power-btn--off'"
            @click="props.handlePowerClick"
          >
            <i class="fa-solid fa-power-off"></i>
          </button>
        </div>

        <div class="light-item-sliders px-2">
          <ListGroupItem class="light-item-slider-row light-item-slider-row--bright">
            <Slider
              name="밝기"
              id="color-bright"
              layout="stacked"
              thin
              filled-track
              track-variant="bright"
              :min="10"
              :max="100"
              :step="10"
              :default="lightBright"
              :disabled="!deviceState(1)"
              value-suffix="%"
              v-on:onSliderChanged="onBrightChanged"
            />
          </ListGroupItem>
          <ListGroupItem class="light-item-slider-row light-item-slider-row--temp">
            <Slider
              name="색온도"
              id="color-temp-modal"
              layout="stacked"
              thin
              filled-track
              track-variant="temp"
              :min="0"
              :max="20"
              :step="2"
              :default="lightColor"
              :disabled="!deviceState(1)"
              v-on:onSliderChanged="onColorChanged"
            />
          </ListGroupItem>
        </div>

        <div class="light-item-actions-wrap px-2" v-if="isOwnerZone">
          <ListGroupItem class="light-item-slider-row">
            <div class="light-item-actions d-flex gap-2 w-100">
              <button
                v-if="supportsLightButtonMapping"
                type="button"
                class="btn btn-primary light-item-action-btn"
                @click="onEditLightClick"
              >
                조명 연결
              </button>
              <button
                type="button"
                class="btn btn-primary light-item-action-btn"
                @click="onAddVirtualDeviceClick"
              >
                가상버튼 추가
              </button>
            </div>
          </ListGroupItem>
        </div>
      </div>
    </div>
  </PopDialog2>
</template>

<script setup>
import { ref, defineProps, computed, getCurrentInstance, reactive } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { DeviceType, PowerState, ButtonConfig } from '@/common/constants';
import Axios, { VirtualDeviceApi } from '@/api';
import useMqttControl from '@/common/hooks/useMqttControl';
import PopDialog2 from '@/common/components/popup/PopDialog2.vue';
import Slider from '@/common/components/widgets/Slider.vue';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import { supportsSensorAndAoLink } from '@/common/utils/controllerDisplayName';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const router = useRouter();
const props = defineProps([
  'visible',
  'cancelCallback',
  'roomName',
  'roomId',
  'handlePowerClick'
]);

const zone = computed(() => {
  if (store.state.home.zones.length > 0) {
    return store.getters['home/getCurrentZone'];
  } else return null;
});
const isOwnerZone = computed(() => {
  return zone.value?.ownerId != undefined
    && store.getters['account']?._id != undefined
    && zone.value?.ownerId === store.getters['account']?._id;
});
const group = computed(() => {
  return zone.value.groups.find((g) => g._id === props.roomId);
});
const devices = computed(() => {
  return group.value ? group.value.devices : undefined;
});

const lightDevices = computed(() => {
  return devices.value.filter(
    (d) => d.type === 1,
  );
});

/** 조명버튼 매핑(조명 편집): M24 V4 이상, M22(V3), M02(V2) */
const supportsLightButtonMapping = computed(() => {
  const controllers = zone.value?.controllers || [];
  const controllerIds = new Set(
    (lightDevices.value || [])
      .filter((d) => !d?.isVirtual && d?.controllerId)
      .map((d) => d.controllerId),
  );
  if (controllerIds.size === 0) {
    return false;
  }
  return controllers.some(
    (c) => controllerIds.has(c._id) && supportsSensorAndAoLink(c),
  );
});

const lightBright = computed(() => {
  if (lightDevices.value.length === 0) {
    return 0;
  }

  let bright = 0;
  lightDevices.value.forEach((device) => {
    if (device.item?.bright > bright) {
      bright = device.item?.bright;
    }
  });
  return bright;
});
const lightColor = computed(() => {
  if (lightDevices.value.length === 0) {
    return 0;
  }

  let color = 0;
  lightDevices.value.forEach((device) => {
    if (device.item?.color > color) {
      color = device.item?.color;
    }
  });
  return color;
});

const deviceState = (type) => {
  return devices.value
    .filter((d) => d.type === type)
    .some((d) => d.item.onoff === PowerState.On);
};

const isLightOn = computed(() => deviceState(DeviceType.Lamp));

const onCancelClick = () => {
  if (props.cancelCallback) {
    props.cancelCallback();
  }
};

const onBrightChanged = async (e) => {
  if (zone.value.isEditMode) {
    emitter.emit('showPopAlert', {
      description: '세팅중인 영역의 단말은 제어 할 수 없습니다.',
    });
    return;
  }

  const devices = lightDevices.value.filter(device => !device.isVirtual);

  emitter.emit('showLoading');

  const byController = {};
  for (const device of devices) {
    const cid = device.controllerId;
    if (!byController[cid]) {
      byController[cid] = { devices: [], ports: [] };
    }
    byController[cid].devices.push(device);
    const ports = device.item?.ports ?? [];
    byController[cid].ports.push(...(Array.isArray(ports) ? ports : [ports]));
  }

  for (const [controllerId, { devices: groupDevices, ports: flatPorts }] of Object.entries(byController)) {
    const hasOnVirtual = groupDevices.some((d) => d?.onVirtual === true);
    const deviceIds = groupDevices.map((d) => d._id);
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];
    for (const device of groupDevices) {
      const ports = Array.isArray(device.item?.ports) ? device.item.ports : (device.item?.ports != null ? [device.item.ports] : []);
      const onoff = device.item?.onoff ?? 0;
      const bright = parseInt(e);
      const color = device.item?.color ?? 0;
      for (const p of ports) {
        portsArr.push(p);
        onoffArr.push(onoff);
        brightArr.push(bright);
        colorArr.push(color);
      }
    }
    const data = {
      ports: portsArr,
      onoff: onoffArr,
      bright: brightArr,
      color: colorArr,
    };
    mqttControl.onPublishBatch(controllerId, deviceIds, {
      serial: controllerId,
      cmd: 'cs',
      type: DeviceType.Lamp,
      data: data,
    }, { 'bright': parseInt(e) });

    if (hasOnVirtual) {
      try {
        const result = await Axios.get(VirtualDeviceApi.VIRTUAL_DEVICES_BY_MEMBERS, {
          deviceIds: deviceIds.join(','),
        });
        const virtualDevices = result?.resultData || [];
        const virtualDeviceIds = new Set(virtualDevices.map((vd) => String(vd?._id)));
        const targetVirtualDevices = lightDevices.value.filter(
          (d) =>
            d?.isVirtual === true &&
            d?.item?.onoff === 1 &&
            virtualDeviceIds.has(String(d?._id)),
        );
        targetVirtualDevices.forEach((vd) => {
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: vd._id,
            data: { item: { onoff: 0 } },
          });
        });
      } catch (err) {
        console.error('virtualDevices(byMembers) 조회 실패:', err);
      }
    }

    console.log(controllerId, deviceIds, data, { 'bright': parseInt(e) });
    await sleep(50);
  }

  setTimeout(() => emitter.emit('hideLoading'), devices.length > 1 ? ButtonConfig.delayBatch : ButtonConfig.delay);
};

const onColorChanged = async (e) => {
  if (zone.value.isEditMode) {
    emitter.emit('showPopAlert', {
      description: '세팅중인 영역의 단말은 제어 할 수 없습니다.',
    });
    return;
  }

  const devices = lightDevices.value.filter(device => !device.isVirtual);

  emitter.emit('showLoading');

  const byController = {};
  for (const device of devices) {
    const cid = device.controllerId;
    if (!byController[cid]) {
      byController[cid] = { devices: [], ports: [] };
    }
    byController[cid].devices.push(device);
    const ports = device.item?.ports ?? [];
    byController[cid].ports.push(...(Array.isArray(ports) ? ports : [ports]));
  }

  for (const [controllerId, { devices: groupDevices, ports: flatPorts }] of Object.entries(byController)) {
    const hasOnVirtual = groupDevices.some((d) => d?.onVirtual === true);
    const deviceIds = groupDevices.map((d) => d._id);
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];
    for (const device of groupDevices) {
      const ports = Array.isArray(device.item?.ports) ? device.item.ports : (device.item?.ports != null ? [device.item.ports] : []);
      const onoff = device.item?.onoff ?? 0;
      const bright = device.item?.bright ?? 0;
      const color = parseInt(e);
      for (const p of ports) {
        portsArr.push(p);
        onoffArr.push(onoff);
        brightArr.push(bright);
        colorArr.push(color);
      }
    }
    const data = {
      ports: portsArr,
      onoff: onoffArr,
      bright: brightArr,
      color: colorArr,
    };
    mqttControl.onPublishBatch(controllerId, deviceIds, {
      serial: controllerId,
      cmd: 'cs',
      type: DeviceType.Lamp,
      data: data,
    }, { 'color': parseInt(e) });

    if (hasOnVirtual) {
      try {
        const result = await Axios.get(VirtualDeviceApi.VIRTUAL_DEVICES_BY_MEMBERS, {
          deviceIds: deviceIds.join(','),
        });
        const virtualDevices = result?.resultData || [];
        const virtualDeviceIds = new Set(virtualDevices.map((vd) => String(vd?._id)));
        const targetVirtualDevices = lightDevices.value.filter(
          (d) =>
            d?.isVirtual === true &&
            d?.item?.onoff === 1 &&
            virtualDeviceIds.has(String(d?._id)),
        );
        targetVirtualDevices.forEach((vd) => {
          store.commit('home/updateDevice', {
            groupId: props.roomId,
            id: vd._id,
            data: { item: { onoff: 0 } },
          });
        });
      } catch (err) {
        console.error('virtualDevices(byMembers) 조회 실패:', err);
      }
    }

    console.log(controllerId, deviceIds, data, { 'color': parseInt(e) });
    await sleep(50);
  }

  setTimeout(() => emitter.emit('hideLoading'), devices.length > 1 ? ButtonConfig.delayBatch : ButtonConfig.delay);
};

const onAddVirtualDeviceClick = () => {
  const hasVirtualOn = (devices.value || []).some((d) => d?.isVirtual === true && d?.item?.onoff == 1);
  if (hasVirtualOn) {
    emitter.emit('showPopAlert', {
      description: '가상 스위치가 켜져 있을 때는\n가상 스위치를 추가/수정할 수 없습니다.',
    });
    return;
  }
  router.push(`/virtual/light/${props.roomId}`);
}

const onEditLightClick = () => {
  const hasVirtualOn = (devices.value || []).some((d) => d?.isVirtual === true && d?.item?.onoff == 1);
  if (hasVirtualOn) {
    emitter.emit('showPopAlert', {
      description: '가상 스위치가 켜져 있을 때는\n조명 편집을 할 수 없습니다.',
    });
    return;
  }
  router.push(`/settings/light/groups/${props.roomId}/buttons`);
}
</script>

<style scoped>
.light-item-modal-content {
  padding-bottom: 1.2rem;
}

.light-item-modal-top {
  --light-modal-card-top-offset: 1.05rem;
  --light-modal-header-overlap: 2.875rem;
  --light-modal-title-zone: 5.25rem;
  --light-modal-power-size: 6rem;
  --light-modal-power-bottom: 0.75rem;

  position: relative;
  margin-top: calc(-1 * var(--light-modal-header-overlap));
  height: calc(
    var(--light-modal-title-zone) +
    var(--light-modal-power-size) +
    var(--light-modal-power-bottom)
  );
  margin-bottom: 0.5rem;
  width: 100%;
  pointer-events: none;
}

.light-item-modal-title {
  position: absolute;
  top: calc((var(--light-modal-card-top-offset) + var(--light-modal-title-zone)) / 2);
  left: 0;
  right: 0;
  transform: translateY(-50%);
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  line-height: 1.3;
  pointer-events: none;
}

.light-item-power-btn {
  position: absolute;
  bottom: var(--light-modal-power-bottom);
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  pointer-events: auto;
  width: var(--light-modal-power-size);
  height: var(--light-modal-power-size);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  transition: background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
}

.light-item-power-btn i {
  font-size: 1.75rem;
  line-height: 1;
}

.light-item-power-btn--on {
  background: radial-gradient(circle at 50% 42%, #fff9ee 0%, #ffecc0 48%, #ffd27a 100%);
  border: 2px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    0 0 0 1px rgba(255, 196, 110, 0.35),
    0 6px 20px rgba(255, 168, 50, 0.42),
    inset 0 2px 4px rgba(255, 255, 255, 0.75);
  color: #5c5348;
}

.light-item-power-btn--off {
  background: radial-gradient(circle at 50% 42%, #f8f9fa 0%, #eceff1 55%, #e2e6ea 100%);
  border: 2px solid #e5e7eb;
  box-shadow:
    inset 0 2px 5px rgba(0, 0, 0, 0.06),
    0 2px 6px rgba(0, 0, 0, 0.04);
  color: #adb5bd;
}

.light-item-sliders {
  width: 100%;
  max-width: 100%;
  margin-top: 0.1rem;
}

.light-item-slider-row {
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
  border: none;
}

.light-item-slider-row--bright {
  padding-bottom: 0.35rem;
}

.light-item-slider-row--temp {
  padding-top: 0.15rem;
  margin-top: 0.2rem;
  padding-bottom: 0.25rem;
}

.light-item-actions-wrap {
  margin-top: 2.85rem;
  padding-bottom: 0.5rem;
}

.light-item-action-btn {
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
}
</style>
